import { useCallback, useEffect, useState } from 'react';

import {
  listSpvs, createSpv, startOnboarding, refreshOnboarding, similarNamesFrom,
  type SpvProfile, type OnboardingStatus, type SimilarSpvNames,
} from '../../lib/spv';
import { ApiError } from '../../lib/apiClient';
import { formatDateShort } from '../../lib/format';
import './AdminPages.css';

/**
 * SPV Connect accounts: the entities that receive net proceeds.
 *
 * An SPV is a legal entity formed around a production, not the organizer behind it. One
 * promoter may run several, and a production spanning multiple dates is one entity across
 * many events, which is why these are managed here as records in their own right rather
 * than as a field on an event or a raise.
 *
 * NOTHING ON THIS SCREEN IS PRESET. Every SPV's legal name is typed by an admin and every
 * Stripe account is created against that specific record. There is no default entity, no
 * example name in a placeholder that could be saved by accident, and no shared account:
 * many SPVs run at once and each one's money must go to its own destination.
 *
 * The status shown is Stripe's, not ours. An account can look finished and still be
 * unable to receive a transfer, so the badge reflects payouts_enabled rather than whether
 * someone completed a form.
 */

const STATUS: Record<OnboardingStatus, { label: string; tone: string; hint: string }> = {
  not_started: {
    label: 'Not started', tone: 'badge--muted',
    hint: 'No Stripe account yet. Start onboarding to create one for this entity.',
  },
  pending: {
    label: 'Onboarding incomplete', tone: 'badge--warn',
    hint: 'Stripe has the account but still needs information before it will accept transfers.',
  },
  complete: {
    label: 'Ready for transfers', tone: 'badge--ok',
    hint: 'Stripe will accept net proceeds for this entity.',
  },
  restricted: {
    label: 'Restricted by Stripe', tone: 'badge--danger',
    hint: 'Stripe has disabled payouts, usually for missing verification. Transfers will fail until it is resolved.',
  },
};

function SpvRow({ spv, onChanged }: { spv: SpvProfile; onChanged: () => void }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const meta = STATUS[spv.onboardingStatus] ?? STATUS.not_started;

  async function run(fn: () => Promise<unknown>) {
    setBusy(true);
    setError(null);
    try { await fn(); onChanged(); } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Something went wrong.');
    } finally { setBusy(false); }
  }

  return (
    <article className="net-row">
      <div className="net-row__main">
        <span className="net-row__name">{spv.legalEntityName}</span>
        <div className="net-row__badges">
          <span className={`badge ${meta.tone}`}>{meta.label}</span>
          {spv.eventCount != null && (
            <span className="badge badge--muted">
              {spv.eventCount} {spv.eventCount === 1 ? 'event' : 'events'}
            </span>
          )}
        </div>
        <span className="opp-reg__hint">{meta.hint}</span>

        {/* A restriction is only useful if it says what to do about it. Stripe names the
            reason and the outstanding requirements on every account update, so they are
            shown here rather than leaving an admin with a red badge and no next step. */}
        {!spv.canReceiveTransfers && spv.stripeConnectAccountId && (
          <div className="spv-alert" role="status">
            <strong className="spv-alert__title">
              {spv.onboardingStatus === 'restricted'
                ? 'Stripe has disabled payouts for this entity'
                : 'This entity cannot receive transfers yet'}
            </strong>
            <p className="spv-alert__body">
              Settlements for its events will record a pending payout and transfer nothing
              until this is resolved. No money is lost; it stays in the platform balance.
            </p>
            {spv.stripeDisabledReason && (
              <p className="spv-alert__body mono">Reason: {spv.stripeDisabledReason}</p>
            )}
            {spv.requirementsDue.length > 0 && (
              <>
                <p className="spv-alert__body">Stripe is waiting for:</p>
                <ul className="spv-alert__list">
                  {spv.requirementsDue.map((r) => <li key={r} className="mono">{r}</li>)}
                </ul>
              </>
            )}
          </div>
        )}
        <span className="opp-reg__hint mono">
          {spv.stripeConnectAccountId ?? 'No Stripe account yet'}
          {spv.contactEmail ? ` · ${spv.contactEmail}` : ''}
          {` · added ${formatDateShort(spv.createdAt)}`}
        </span>
        {error && <p className="adm-error" role="alert">{error}</p>}
      </div>

      <div className="net-row__actions">
        <button type="button" className="button button--primary adm-btn-sm" disabled={busy}
          onClick={() => void run(async () => {
            const { url } = await startOnboarding(spv.id);
            // Stripe's hosted flow. The link is short lived, which is why it is fetched
            // on click rather than rendered as an anchor.
            window.location.assign(url);
          })}>
          {spv.stripeConnectAccountId ? 'Continue onboarding' : 'Start onboarding'}
        </button>
        {spv.stripeConnectAccountId && (
          <button type="button" className="button button--ghost adm-btn-sm" disabled={busy}
            onClick={() => void run(() => refreshOnboarding(spv.id))}>
            {busy ? 'Checking…' : 'Check status'}
          </button>
        )}
      </div>
    </article>
  );
}

export function AdminSpvs() {
  const [rows, setRows] = useState<SpvProfile[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [saving, setSaving] = useState(false);
  const [similar, setSimilar] = useState<SimilarSpvNames | null>(null);
  const [linked, setLinked] = useState<string | null>(null);

  const load = useCallback(() => {
    listSpvs()
      .then((r) => { setRows(r); setError(null); })
      .catch((err: unknown) =>
        setError(err instanceof ApiError ? err.message : 'Could not load SPVs.'));
  }, []);

  useEffect(load, [load]);

  // Stripe sends the admin back here after the hosted flow, so pick up the new state
  // rather than showing whatever was true before they left.
  useEffect(() => {
    const returned = new URLSearchParams(window.location.search).get('returned');
    if (!returned) return;
    refreshOnboarding(returned).then(load).catch(() => load());
  }, [load]);

  async function add(confirmCreate = false) {
    if (!name.trim()) return;
    setSaving(true);
    setError(null);
    try {
      const res = await createSpv({
        legalEntityName: name.trim(), contactEmail: email.trim() || null, confirmCreate });
      // The engine links an entity that already exists rather than making a second one.
      // Saying so matters here: the list below will not have grown, and without this the
      // screen looks like it ignored the click.
      setLinked(res.created ? null : res.legalEntityName);
      setSimilar(null); setName(''); setEmail(''); load();
    } catch (err) {
      const ask = similarNamesFrom(err);
      if (ask) setSimilar(ask);
      else setError(err instanceof ApiError ? err.message : 'Could not create the SPV.');
    } finally { setSaving(false); }
  }

  return (
    <div className="adm-page" data-surface="invest">
      <header className="adm-header">
        <div>
          <h1 className="adm-title">SPV payout accounts</h1>
          <p className="adm-lede">
            The legal entity that receives an event's net proceeds. Each production has its
            own entity and its own Stripe account, so add one record per SPV and onboard it
            separately. A raise cannot open until its event's SPV is ready for transfers.
          </p>
        </div>
      </header>

      {error && <div className="alert alert--error" role="alert">{error}</div>}

      <section className="net-panel">
        <h2 className="net-panel__title">Add an SPV</h2>
        <div className="wiz-grid">
          <label className="field wiz-field--wide">
            <span className="field__label">Registered legal entity name *</span>
            <input className="field__input" type="text" value={name}
              onChange={(e) => setName(e.target.value)} />
          </label>
          <label className="field wiz-field--wide">
            <span className="field__label">Contact email for Stripe (optional)</span>
            <input className="field__input" type="email" value={email}
              onChange={(e) => setEmail(e.target.value)} />
          </label>
        </div>
        <button type="button" className="button button--primary"
          disabled={!name.trim() || saving} onClick={() => void add()}>
          {saving ? 'Adding…' : 'Add SPV'}
        </button>

        {linked && (
          <p className="adm-hint">
            <strong>{linked}</strong> already existed, so it was used rather than
            duplicated. One legal entity has one record and one Stripe account.
          </p>
        )}

        {similar && (
          <div className="adm-spv-similar" role="alert">
            <p className="adm-spv-similar__q">
              <strong>{similar.typed}</strong> is close to an entity that already exists:{' '}
              {similar.candidates.map((c) => c.legalEntityName).join(', ')}.
            </p>
            <p className="adm-spv-help">
              If one of those is the company you meant, it is already here and needs no new
              record. Creating this one makes a separate company with its own Stripe
              account and its own payout destination.
            </p>
            <div className="adm-actions">
              <button type="button" className="button button--ghost adm-btn-sm"
                disabled={saving} onClick={() => setSimilar(null)}>
                Never mind
              </button>
              <button type="button" className="button button--ghost adm-btn-sm sup-danger"
                disabled={saving} onClick={() => void add(true)}>
                Create {similar.typed} as a separate company
              </button>
            </div>
          </div>
        )}
      </section>

      {(rows ?? []).some((s) => s.stripeConnectAccountId && !s.canReceiveTransfers) && (
        <div className="alert alert--error" role="alert">
          <strong>
            {(rows ?? []).filter((s) => s.stripeConnectAccountId && !s.canReceiveTransfers).length}
            {' '}
            {(rows ?? []).filter((s) => s.stripeConnectAccountId && !s.canReceiveTransfers).length === 1
              ? 'entity cannot' : 'entities cannot'} receive transfers.
          </strong>
          {' '}
          Stripe updates this automatically, so a green entity can turn red without anyone
          touching it. Raises will not open and settlements will not pay out until it is fixed.
        </div>
      )}

      {rows == null && <p className="adm-status">Loading…</p>}
      {rows != null && rows.length === 0 && (
        <p className="adm-status adm-muted">
          No SPVs yet. Add the entity that will receive proceeds for a production.
        </p>
      )}

      <div className="adm-list">
        {(rows ?? []).map((spv) => <SpvRow key={spv.id} spv={spv} onChanged={load} />)}
      </div>
    </div>
  );
}
